import {
    Atom,
    Braces,
    Building2,
    Cloud,
    Code2,
    Compass,
    Container,
    Database,
    DatabaseZap,
    FileCode2,
    Flame,
    GitBranch,
    Github,
    GraduationCap,
    HeartHandshake,
    HeartPulse,
    Landmark,
    Layers,
    Lightbulb,
    PenTool,
    Plane,
    Rocket,
    ScanSearch,
    Server,
    ShieldCheck,
    ShoppingBag,
    Smartphone,
    Sparkles,
    Store,
    Terminal,
    Triangle,
    TrendingUp,
    Truck,
    UtensilsCrossed,
    Zap,
} from "lucide-react";

export const BRAND = {
    name: "Zenithix",
    tagline: "Where Innovation Meets Growth",
    email: "zenithixservices@gmail.com",
    phones: ["+91 93602 68909", "+91 63798 22744"],
    address: "Chennai, Tamil Nadu 600024",
};

export const NAV = [
    { label: "Home", path: "/", transition: "ZENITHIX" },
    { label: "About", path: "/about", transition: "ABOUT ZENITHIX" },
    { label: "Services", path: "/services", transition: "OUR SERVICES" },
    { label: "Portfolio", path: "/portfolio", transition: "EXPLORE POSSIBILITIES" },
    { label: "Industries", path: "/industries", transition: "INDUSTRIES" },
    { label: "Process", path: "/process", transition: "OUR PROCESS" },
    { label: "Contact", path: "/contact", transition: "LET'S CONNECT" },
];

export const SERVICES = [
    {
        id: "web",
        icon: Code2,
        num: "01",
        name: "Web Engineering",
        short: "High-performance websites and web apps engineered for speed, scale and conversion.",
        detail:
            "From marketing sites that load in a blink to complex product platforms — we architect React and Next.js systems that stay fast under real-world load.",
        points: ["React / Next.js / TypeScript", "Headless commerce & CMS", "Core Web Vitals obsessed"],
    },
    {
        id: "mobile",
        icon: Smartphone,
        num: "02",
        name: "Mobile Products",
        short: "Native-feel iOS and Android experiences that turn users into advocates.",
        detail:
            "One codebase or fully native — we ship mobile products with buttery 60fps interactions, offline-first data and store-ready polish.",
        points: ["React Native & Flutter", "App Store / Play launches", "Push, offline & analytics"],
    },
    {
        id: "design",
        icon: PenTool,
        num: "03",
        name: "UI/UX Design",
        short: "Interfaces that guide attention and convert curiosity into action.",
        detail:
            "Research-backed flows, motion systems and design languages that make complex products feel obvious — and beautiful.",
        points: ["Product & brand design", "Design systems", "Motion & prototyping"],
    },
    {
        id: "ai",
        icon: Sparkles,
        num: "04",
        name: "AI & Automation",
        short: "Intelligent systems that remove busywork and compound your team's output.",
        detail:
            "LLM copilots, intelligent document pipelines and workflow automation embedded directly into the tools your team already uses.",
        points: ["LLM integration & copilots", "Workflow automation", "Data pipelines"],
    },
    {
        id: "cloud",
        icon: Cloud,
        num: "05",
        name: "Cloud & DevOps",
        short: "Resilient infrastructure built for uptime, velocity and zero-drama deploys.",
        detail:
            "Cloud-native architecture, CI/CD and observability — so releases become routine and incidents become rare.",
        points: ["AWS / GCP architecture", "Kubernetes & CI/CD", "Monitoring & SRE"],
    },
    {
        id: "growth",
        icon: TrendingUp,
        num: "06",
        name: "Growth & Analytics",
        short: "Data engines that measure what matters and multiply what works.",
        detail:
            "Analytics foundations, experimentation programs and conversion optimization that tie every release to revenue.",
        points: ["Product analytics", "A/B testing programs", "SEO & conversion"],
    },
];

export const SOLUTION_CONCEPTS = [
    {
        id: "commerce", num: "01", name: "E-COMMERCE STORES", category: "Online commerce",
        image: "https://images.unsplash.com/photo-1727407209320-1fa6ae60ee05?auto=format&fit=crop&w=1400&q=80",
        bg: "#050D1F",
        desc: "Give your products a home with a storefront built around how your customers shop.",
        features: ["Product catalogues", "Secure checkout", "Order management"],
    },
    {
        id: "website", num: "02", name: "BUSINESS WEBSITES", category: "Brand presence",
        image: "https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?auto=format&fit=crop&w=1400&q=80",
        bg: "#04101F",
        desc: "Turn your story into a clear, memorable website that helps people take the next step.",
        features: ["Responsive layouts", "Content management", "Enquiry forms"],
    },
    {
        id: "mobile", num: "03", name: "MOBILE APPLICATIONS", category: "Mobile experiences",
        image: "https://images.unsplash.com/photo-1771922748624-b205cf5d002d?auto=format&fit=crop&w=1400&q=80",
        bg: "#030B18",
        desc: "Bring your idea to the small screen with intuitive journeys designed for everyday use.",
        features: ["iOS & Android", "Push notifications", "User accounts"],
    },
    {
        id: "dashboard", num: "04", name: "ADMIN DASHBOARDS", category: "Business insights",
        image: "https://images.unsplash.com/photo-1543797414-a0c3ad076f7c?auto=format&fit=crop&w=1400&q=80",
        bg: "#071426",
        desc: "Bring your data and daily operations together in one workspace your team can understand.",
        features: ["Reporting views", "Role-based access", "Data integrations"],
    },
    {
        id: "booking", num: "05", name: "BOOKING PLATFORMS", category: "Scheduling & reservations",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
        bg: "#040E1C",
        desc: "Make it easier for customers to find a time, book a service and stay informed.",
        features: ["Availability calendars", "Online reservations", "Booking reminders"],
    },
    {
        id: "automation", num: "06", name: "AI-POWERED WORKFLOWS", category: "Connected operations",
        image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1400&q=80",
        bg: "#050F22",
        desc: "Connect repetitive tasks with thoughtful automation and AI assistance where it makes sense.",
        features: ["AI assistants", "Workflow automation", "Tool integrations"],
    },
    {
        id: "software", num: "07", name: "CUSTOM BUSINESS SOFTWARE", category: "Built around your team",
        image: "https://images.unsplash.com/photo-1603566234499-85676f87022f?auto=format&fit=crop&w=1400&q=80",
        bg: "#041224",
        desc: "Shape a workspace around your processes, instead of shaping your business around a tool.",
        features: ["Custom workflows", "Team collaboration", "Internal tools"],
    },
];

export const INDUSTRIES = [
    { id: "ecommerce", icon: ShoppingBag, name: "E-commerce", desc: "Storefronts and commerce infrastructure that convert.", grad: "from-electric/25 via-electric/5" },
    { id: "education", icon: GraduationCap, name: "Education", desc: "Learning platforms that keep students coming back.", grad: "from-sky-400/25 via-sky-400/5" },
    { id: "healthcare", icon: HeartPulse, name: "Healthcare", desc: "Compliant, human digital health experiences.", grad: "from-cyan-400/25 via-cyan-400/5" },
    { id: "realestate", icon: Building2, name: "Real Estate", desc: "Property platforms and virtual touring tech.", grad: "from-blue-400/25 via-blue-400/5" },
    { id: "logistics", icon: Truck, name: "Logistics", desc: "Supply-chain software with live visibility.", grad: "from-indigo-400/25 via-indigo-400/5" },
    { id: "retail", icon: Store, name: "Retail", desc: "Omnichannel tools bridging store and screen.", grad: "from-electric/25 via-electric/5" },
    { id: "food", icon: UtensilsCrossed, name: "Food & Delivery", desc: "Ordering and dispatch that scales with hunger.", grad: "from-teal-400/25 via-teal-400/5" },
    { id: "finance", icon: Landmark, name: "Finance", desc: "Banking-grade security meets consumer-grade UX.", grad: "from-sky-500/25 via-sky-500/5" },
    { id: "travel", icon: Plane, name: "Travel", desc: "Booking and journey products people love.", grad: "from-cyan-300/25 via-cyan-300/5" },
    { id: "startups", icon: Rocket, name: "Startups", desc: "MVP to scale-up, shipped at founder speed.", grad: "from-blue-300/25 via-blue-300/5" },
];

export const PROCESS = [
    { num: "01", icon: ScanSearch, name: "Discover", desc: "Deep-dive workshops to map goals, users and constraints before a pixel is placed." },
    { num: "02", icon: Compass, name: "Strategy", desc: "A roadmap that ties every feature and sprint to a measurable business outcome." },
    { num: "03", icon: PenTool, name: "Design", desc: "Prototypes and design systems validated with real users, not opinions." },
    { num: "04", icon: Code2, name: "Develop", desc: "Clean, scalable code shipped in weekly increments you can click and test." },
    { num: "05", icon: ShieldCheck, name: "Test", desc: "Automated QA across devices, flows and the edge cases nobody thinks about." },
    { num: "06", icon: Rocket, name: "Launch", desc: "Zero-drama releases with monitoring, analytics and rollback baked in." },
    { num: "07", icon: TrendingUp, name: "Grow", desc: "Post-launch iteration that compounds: measure, learn, optimize, repeat." },
];

export const WHY = [
    { num: "01", icon: Lightbulb, name: "Business-First Thinking", desc: "Every pixel and every query is tied to a commercial outcome — never vanity work." },
    { num: "02", icon: Code2, name: "Engineering Excellence", desc: "Senior teams, rigorous reviews and architecture chosen for year five, not demo day." },
    { num: "03", icon: HeartHandshake, name: "Radical Transparency", desc: "Weekly demos, open backlogs and honest timelines. You always know where things stand." },
    { num: "04", icon: TrendingUp, name: "Growth Obsession", desc: "We measure success in your metrics — revenue, retention, velocity — not deliverables." },
];

export const TECH_ROW1 = [
    { name: "React", icon: Atom },
    { name: "Next.js", icon: Triangle },
    { name: "TypeScript", icon: FileCode2 },
    { name: "Flutter", icon: Layers },
    { name: "React Native", icon: Smartphone },
    { name: "Python", icon: Terminal },
    { name: "FastAPI", icon: Zap },
    { name: "Node.js", icon: Server },
];

export const TECH_ROW2 = [
    { name: "MongoDB", icon: Database },
    { name: "PostgreSQL", icon: DatabaseZap },
    { name: "Firebase", icon: Flame },
    { name: "AWS", icon: Cloud },
    { name: "Docker", icon: Container },
    { name: "GitHub", icon: Github },
    { name: "Bitbucket", icon: GitBranch },
];

export const STATS = [
    { value: 120, suffix: "+", label: "Projects shipped" },
    { value: 98, suffix: "%", label: "Client retention" },
    { value: 40, suffix: "+", label: "Product specialists" },
    { value: 12, suffix: "", label: "Industries served" },
];

export const SOCIALS = [
    { name: "GitHub", href: "https://github.com" },
    { name: "Twitter", href: "https://twitter.com" },
    { name: "LinkedIn", href: "https://linkedin.com" },
    { name: "Dribbble", href: "https://dribbble.com" },
];
